import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { artists, services, timeSlots } from '../mock';
import { CalendarIcon, Check } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    artist: '',
    service: '',
    date: null,
    time: '',
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Check if an artist was pre-selected from the Artists section
    const selectedArtist = sessionStorage.getItem('selectedArtist');
    if (selectedArtist) {
      setFormData(prev => ({ ...prev, artist: selectedArtist }));
      sessionStorage.removeItem('selectedArtist');
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.artist || !formData.service || !formData.date || !formData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send booking to backend API
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        artist: formData.artist,
        service: formData.service,
        date: format(formData.date, 'PPP'),
        time: formData.time,
        description: formData.description || ''
      };

      await axios.post(`${BACKEND_URL}/api/bookings`, bookingData);
      
      toast.success('Booking request submitted! We\'ll confirm via email within 24 hours.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        artist: '',
        service: '',
        date: null,
        time: '',
        description: ''
      });
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-20 px-6 bg-black">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-4 uppercase tracking-tight">
            Book Your <span className="text-red-600">Appointment</span>
          </h2>
          <p className="text-xl text-gray-400">
            Choose your artist and schedule your session
          </p>
        </div>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-2xl font-black text-white uppercase">
              Appointment Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white font-bold uppercase text-sm">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-red-600"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white font-bold uppercase text-sm">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-red-600"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white font-bold uppercase text-sm">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 focus:border-red-600"
                  required
                />
              </div>

              {/* Artist and Service Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-white font-bold uppercase text-sm">
                    Select Artist *
                  </Label>
                  <Select value={formData.artist} onValueChange={(value) => handleSelectChange('artist', value)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white focus:border-red-600">
                      <SelectValue placeholder="Choose an artist" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                      {artists.map((artist) => (
                        <SelectItem key={artist.id} value={artist.name} className="focus:bg-red-600 focus:text-white">
                          {artist.name} - {artist.specialty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white font-bold uppercase text-sm">
                    Service Type *
                  </Label>
                  <Select value={formData.service} onValueChange={(value) => handleSelectChange('service', value)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white focus:border-red-600">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.name} className="focus:bg-red-600 focus:text-white">
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date and Time Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-white font-bold uppercase text-sm">
                    Preferred Date *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700 hover:text-white focus:border-red-600"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-red-600" />
                        {formData.date ? format(formData.date, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-zinc-800 border-zinc-700">
                      <Calendar
                        mode="single"
                        selected={formData.date}
                        onSelect={handleDateChange}
                        disabled={(date) => date < new Date() || date.getDay() === 0}
                        className="bg-zinc-800 text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-white font-bold uppercase text-sm">
                    Preferred Time *
                  </Label>
                  <Select value={formData.time} onValueChange={(value) => handleSelectChange('time', value)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white focus:border-red-600">
                      <SelectValue placeholder="Choose a time" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot} className="focus:bg-red-600 focus:text-white">
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-white font-bold uppercase text-sm">
                  Design Description (Optional)
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your tattoo idea, size, placement, and any reference images you'll bring..."
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500 min-h-32 focus:border-red-600"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-black uppercase tracking-wider transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Check className="w-5 h-5" />
                    Submit Booking Request
                  </span>
                )}
              </Button>

              <p className="text-gray-500 text-sm text-center">
                * We'll contact you within 24 hours to confirm your appointment
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default Booking;
